import { ResolvedSessionSlot } from '../utils/sessionPlan';
import {
  ClassroomTeam,
  PortfolioEntry,
  coalescePortfolioEntries,
  portfolioEntryMatchesSlot,
  visibleAiCreations,
  visiblePortfolioPhotos,
} from './types';
import {
  creationDisplayName,
  fetchBytes,
  filenameSafe,
  photoExtension,
  portfolioDownloadStem,
  portfolioPhotoFilename,
  slugifyName,
  triggerBlobDownload,
} from './aiCreation';
import { encodeUtf8, zipStoreFiles } from './zipStore';

export interface ClassPhotoArchiveItem {
  name: string;
  url?: string;
  bytes?: Uint8Array;
}

export function classPhotoArchiveStem(squadName: string, slot: ResolvedSessionSlot): string {
  return `${portfolioDownloadStem({
    teamName: 'all-teams',
    squadName,
    classDate: slot.date,
    className: `Class ${slot.index} ${slot.levelTitle}`,
  })}_portfolio`;
}

function urlShortcut(url: string): Uint8Array {
  return encodeUtf8(`[InternetShortcut]\r\nURL=${url}\r\n`);
}

function aiArtifactFilename(entry: PortfolioEntry, index: number): string {
  const creation = visibleAiCreations(entry.aiVersions)[index];
  const displayName = filenameSafe(creationDisplayName({
    filename: creation.filename,
    url: creation.url,
    caption: creation.caption,
    type: creation.type,
  })) || `AI artifact ${index + 1}`;
  const isWebArtifact = ['game_link', 'claude_artifact', 'link'].includes(creation.type || 'link');
  return `${String(index + 1).padStart(2, '0')}_${displayName}${isWebArtifact && !displayName.toLowerCase().endsWith('.url') ? '.url' : ''}`;
}

export function classPhotoArchiveItems(args: {
  squadName: string;
  slot: ResolvedSessionSlot;
  teams: ClassroomTeam[];
  entries: PortfolioEntry[];
}): ClassPhotoArchiveItem[] {
  const teamById = new Map(args.teams.map((team) => [team.id, team]));
  const matchingEntries = coalescePortfolioEntries(
    args.entries.filter((entry) => portfolioEntryMatchesSlot(entry, args.slot))
  ).sort((a, b) => {
    const teamA = teamById.get(a.teamId)?.name || 'Unknown team';
    const teamB = teamById.get(b.teamId)?.name || 'Unknown team';
    return teamA.localeCompare(teamB);
  });
  const root = classPhotoArchiveStem(args.squadName, args.slot);

  return matchingEntries.flatMap((entry) => {
    const teamName = teamById.get(entry.teamId)?.name || 'Unknown team';
    const teamFolder = slugifyName(teamName) || 'unknown-team';
    const photos = visiblePortfolioPhotos(entry.photos).map((photo, index) => ({
      name: `${root}/${teamFolder}/${portfolioPhotoFilename({
        teamName,
        squadName: args.squadName,
        classDate: args.slot.date || entry.date,
        className: `Class ${args.slot.index} ${args.slot.levelTitle}`,
        photoIndex: index + 1,
        ext: photoExtension(photo),
      })}`,
      url: photo.url,
    }));
    const artifacts = visibleAiCreations(entry.aiVersions).flatMap<ClassPhotoArchiveItem>((creation, index) => {
      const name = `${root}/${teamFolder}/ai-artifacts/${aiArtifactFilename(entry, index)}`;
      const isWebArtifact = ['game_link', 'claude_artifact', 'link'].includes(creation.type || 'link');
      if (isWebArtifact && creation.url) {
        return [{ name, bytes: urlShortcut(creation.url) }];
      }
      const assetUrl = creation.url || creation.posterUrl;
      return assetUrl ? [{ name, url: assetUrl }] : [];
    });
    return [...photos, ...artifacts];
  });
}

export async function downloadClassPhotos(args: {
  squadName: string;
  slot: ResolvedSessionSlot;
  teams: ClassroomTeam[];
  entries: PortfolioEntry[];
}): Promise<number> {
  const items = classPhotoArchiveItems(args);
  if (!items.length) return 0;
  const files = await Promise.all(
    items.map(async (item) => ({
      name: item.name,
      bytes: item.bytes || await fetchBytes(item.url as string),
    }))
  );
  triggerBlobDownload(
    zipStoreFiles(files),
    `${classPhotoArchiveStem(args.squadName, args.slot)}.zip`
  );
  return files.length;
}
